import React, { useMemo } from "react";
import { WidgetOverlay } from "../WidgetOverlay";
import { WidgetBaseProps } from "../WidgetContainer";
import { WithQueryWidgetProps } from "./withQueryWidget";
import WidgetSettingsWrapper from "../WidgetSettingsWrapper";
import { WidgetQuery } from "system/widget/Widget";
import { Alert, AlertTitle, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import WidgetMultiqueryDialog from "../WidgetMultiqueryDialog";
import { useDashboard } from "routes/dashboard/Dashboard";
import { usePDFExport, PDFExportOptions } from "utils/usePDFExport";

export default function QueryWidgetInnerContainer(props: WithQueryWidgetProps): JSX.Element {
  const { i18n } = useTranslation();
  const dashboard = useDashboard();
  const { downloadPDF } = usePDFExport();

  const { WrappedComponent, widgetBaseSettings } = props;
  const [settingsDialogIsOpen, setSettingsDialogIsOpen] = React.useState(false);
  const [queryDialogIsOpen, setQueryDialogIsOpen] = React.useState(false);
  const widgetRef = React.useRef<HTMLDivElement>(null);

  const widgetQueryIds = useMemo((): string[] => {
    if (!props.widget.request_info?.queries) {
      return [];
    }
    return props.widget.request_info?.queries?.map((q: WidgetQuery) => q.id) || [];
  }, [props.widget.request_info?.queries]);

  function handleDownloadPDF(options?: PDFExportOptions) {
    return downloadPDF(widgetRef, options ?? {
      filename: `${props.widget.name || 'widget'}-export.pdf`,
    });
  }

  return (<>
    <WidgetOverlay
      {...props as WidgetBaseProps}
      hasSettingsDialog={widgetBaseSettings?.hasSettingsDialog}
      hasQueryDialog={widgetBaseSettings?.hasQueryDialog}
      hideDataExports={widgetBaseSettings?.hideDataExports}
      hideImageExport={widgetBaseSettings?.hideImageExport}
      showPDFExport={widgetBaseSettings?.showPDFExport}
      onSettingsOpened={() => !settingsDialogIsOpen && setSettingsDialogIsOpen(true)}
      onQueryDialogOpened={() => !queryDialogIsOpen && setQueryDialogIsOpen(true)}
      onUpdateWidget={props.onUpdateWidget}
      onDownloadPDF={handleDownloadPDF}
      saveWidgetQueries={props.saveWidgetQueries}
      activeWidgetRequests={props.activeWidgetRequests}
      widgetRef={widgetRef}
    />
    {queryDialogIsOpen &&
      <WidgetMultiqueryDialog
        dashboardDatasetId={dashboard?.template_type ? (dashboard?.dataset_id || undefined) : undefined}
        queryIds={widgetQueryIds}
        onClose={() => {
          setQueryDialogIsOpen(false);
        }}
        onSubmit={(widgetQueries: WidgetQuery[]) => {
          props.saveWidgetQueries(widgetQueries);
          while (props.widgetQueries?.length) {
            props.widgetQueries.pop();
          }
          setQueryDialogIsOpen(false);
        }}
        canSubmitEmptyForm={true}
      />
    }
    <WidgetSettingsWrapper
      {...props}
      settingsDialogIsOpen={settingsDialogIsOpen}
      setSettingsDialogIsOpen={setSettingsDialogIsOpen}
    />
    {(!!props.activeWidgetRequests?.length && WrappedComponent) && (
      <WrappedComponent {...props}
        widgetRef={widgetRef}
      />
    )}
    {(!props.activeWidgetRequests?.length) && (
      <>
      <Typography variant="body2" color="textSecondary" sx={{ textAlign: "center", opacity: 0.7, pb: 0, mb: 0, mt: 0.5 }}>
        {i18n.t(`widget-types.${props.widgetRegistryInfo?.id}.label`)}
      </Typography>
      <Alert severity="warning" sx={{ m: "1rem", mt: 0.5 }} >
        <AlertTitle>{i18n.t("api-request.missing-query-error")}</AlertTitle>
      </Alert>
      </>
    )}
  </>);
}